import React, { useEffect, useState, useRef } from "react";
import Plotly from "plotly.js-dist-min";
import axios from "axios";

// This component now takes the loggedInUser as a prop
const LiveGraph = ({ loggedInUser }) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedDeviceName, setSelectedDeviceName] = useState("");
  const [isLive, setIsLive] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("Initializing...");
  const [graphData, setGraphData] = useState({
    timestamps: [], x: [], y: [], z: []//, tau_c: [], pd: [],
  });
  const [viewCenterTime, setViewCenterTime] = useState(new Date());
  const graphEl = useRef(null);
  
  // --- NEW: State for the date/time picker ---
  const [historicalDateTime, setHistoricalDateTime] = useState(new Date().toISOString().slice(0, 16));


  // Fetch the list of available devices when the user logs in
  useEffect(() => {
    if (!loggedInUser) return; // Don't fetch if no user is logged in

    const fetchDevices = async () => {
      try {
        // --- FIX: Use POST and send the loggedInUser ---
        const response = await axios.post("http://52.66.78.81:4000/api/devices", {
            user: loggedInUser
        });
        const deviceList = response.data || [];
        setDevices(deviceList);
        if (deviceList.length > 0) {
          // If a device is already selected and still exists, keep it. Otherwise, select the first one.
          const selectedStillExists = selectedDevice && deviceList.some((device) => device.device_id === selectedDevice);
          if (!selectedStillExists) {
            setSelectedDevice(deviceList[0].device_id);
            setSelectedDeviceName(deviceList[0].display_name || deviceList[0].device_id);
          } else {
            const current = deviceList.find((device) => device.device_id === selectedDevice);
            setSelectedDeviceName(current?.display_name || current?.device_id || "");
          }
        } else {
            setSelectedDevice(null);
            setSelectedDeviceName("");
        }
      } catch (error) {
        console.error("Error fetching devices:", error);
        setStatusMessage("Error: Could not fetch device list.");
      }
    };
    fetchDevices();
  }, [loggedInUser]);

  // Helper function to process data (Unchanged)
  const processData = (data) => {
    if (!data || data.length === 0) {
        return { timestamps: [], x: [], y: [], z: []}; //, tau_c: [], pd: [] };
    }
    const timestamps = data.map((row) => new Date(row[0]));
    const x = data.map((row) => parseFloat(row[1]));
    const y = data.map((row) => parseFloat(row[2]));
    const z = data.map((row) => parseFloat(row[3]));
    const tau_c = data.map((row) => parseFloat(row[4] || 0));
    const pd = data.map((row) => parseFloat(row[5] || 0));
    return { timestamps, x, y, z}; //, tau_c, pd };
  };

  // Main data fetching logic (Unchanged)
  useEffect(() => {
    if (!selectedDevice) return;
    const current = devices.find((device) => device.device_id === selectedDevice);
    setSelectedDeviceName(current?.display_name || current?.device_id || selectedDevice);

    let interval;
    if (isLive) {
      const fetchLiveData = async () => {
        setIsLoading(true);
        try {
          const response = await axios.get(`http://52.66.78.81:4000/api/device-data/${selectedDevice}`);
          const processed = processData(response.data.data);
          setGraphData(processed);
          setStatusMessage("Live data streaming...");
          if (processed.timestamps.length > 0) {
             setViewCenterTime(processed.timestamps[processed.timestamps.length - 1]);
          }
        } catch (error) {
          console.error("Error fetching live data:", error);
          setStatusMessage("Error fetching live data.");
        } finally {
          setIsLoading(false);
        }
      };
      fetchLiveData();
      interval = setInterval(fetchLiveData, 1000);
    } else {
      const fetchHistoricalData = async () => {
        setIsLoading(true);
        setStatusMessage(`Fetching data around ${viewCenterTime.toLocaleString()}`);
        const endTime = viewCenterTime;
        const startTime = new Date(endTime.getTime() - 60000);
        try {
          const response = await axios.get(
            `http://52.66.78.81:4000/api/historical-data/${selectedDevice}`, {
              params: {
                start: startTime.toISOString(),
                end: endTime.toISOString()
              }
            }
          );
          if (response.data.data && response.data.data.length > 0) {
            setGraphData(processData(response.data.data));
            setStatusMessage(`Graph paused. Double-click to resume live feed.`);
          } else {
            setGraphData({ timestamps: [], x: [], y: [], z: [] }); //, tau_c: [], pd: [] });
            setStatusMessage(`No data found for ${viewCenterTime.toLocaleString()}. Try a different time.`);
          }
        } catch (error) {
          console.error("Error fetching historical data:", error);
          setStatusMessage("Error fetching historical data. Please try again.");
        } finally {
            setIsLoading(false);
        }
      };
      fetchHistoricalData();
    }
    
    return () => clearInterval(interval);

  }, [selectedDevice, isLive, viewCenterTime, devices]);

  // Render graph and set up event listeners (Unchanged)
  useEffect(() => {
    if (!graphEl.current || !selectedDevice) return;

    const traces = [
      { x: graphData.timestamps, y: graphData.z, mode: "lines", name: "X Accel", line: { color: "blue" } },
      { x: graphData.timestamps, y: graphData.y, mode: "lines", name: "Y Accel", line: { color: "red" } },
      { x: graphData.timestamps, y: graphData.x, mode: "lines", name: "Z Accel", line: { color: "green" } } //,
   //   { x: graphData.timestamps, y: graphData.tau_c, mode: "lines", name: "TauC (s)", line: { color: "purple", dash: 'dash' }, yaxis: 'y2' },
    //  { x: graphData.timestamps, y: graphData.pd, mode: "lines", name: "Pd (cm)", line: { color: "orange", dash: 'dot' }, yaxis: 'y2' }
    ];

    const layout = {
      title: `Seismic Data for ${selectedDeviceName || selectedDevice}`,
      xaxis: { title: "Time", type: "date", tickformat: "%H:%M:%S" },
      yaxis: { title: "Acceleration (cm/s²)", titlefont: { color: '#1f77b4' }, tickfont: { color: '#1f77b4' } },
    //  yaxis2: { title: 'EEW Params (TauC, Pd)', titlefont: { color: '#ff7f0e' }, tickfont: { color: '#ff7f0e' }, overlaying: 'y', side: 'right' },
      legend: { x: 1.1, y: 1 }
    };

    Plotly.react(graphEl.current, traces, layout);

    const currentGraphEl = graphEl.current;
    const handleZoom = () => { if (isLive) setIsLive(false); };
    const handleDoubleClick = () => { if (!isLive) setIsLive(true); };
    
    currentGraphEl.on('plotly_relayout', handleZoom);
    currentGraphEl.on('plotly_doubleclick', handleDoubleClick);

    return () => {
      if (currentGraphEl) {
        Plotly.purge(currentGraphEl);
      }
    };
  }, [graphData, selectedDevice, isLive]);

  // --- NEW: Handler for the date/time picker ---
  const handleFetchHistorical = () => {
      if (historicalDateTime) {
          setIsLive(false); // Switch to historical mode
          setViewCenterTime(new Date(historicalDateTime)); // Set the new time to fetch around
      }
  };

  return (
    <div>
       <h2 style={{ textAlign: 'center', color: '#2d3748', marginTop: '40px' }}>Waveform Data Viewer</h2>
      <div style={{ marginBottom: "20px", display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
        <div>
            <label htmlFor="deviceSelector">Select Device: </label>
            <select
              id="deviceSelector"
              value={selectedDevice || ""}
              onChange={(e) => {
                const nextDevice = devices.find((device) => device.device_id === e.target.value);
                setSelectedDevice(e.target.value);  
                setSelectedDeviceName(nextDevice?.display_name || nextDevice?.device_id || e.target.value);
                setIsLive(true);
              }}
              disabled={!selectedDevice}
            >
            {devices.map((device) => (
              <option key={device.device_id} value={device.device_id}>
                {device.display_name || device.device_id}
              </option>
            ))}
          </select>
        </div>
        
        {/* --- NEW: Date/Time picker for historical data --- */}
 
      </div>
 
      <div ref={graphEl} id="graph" style={{ width: "100%", height: "600px" }}></div>
    </div>
  );
};

export default LiveGraph;
